/**
 * Profile data service using Firestore
 * All profile data (addresses, vehicles, insurance, health, etc.) stored in Firestore
 * Only session data (current user, profile ID) stored in localStorage
 *
 * ENCRYPTION: Sensitive fields are encrypted using AES-256-GCM before being stored in Firestore.
 * Data is decrypted when retrieved. Encrypted fields include:
 * - Profile: dateOfBirth
 * - Vehicles: vin, licensePlate
 * - Addresses: wifiPassword, accessCodes
 * - Utility Accounts: accountNumber
 * - OwnerSecurity: dateOfBirth, recoveryEmail, recoveryPhone
 * - Family Members' ImportantDates: date
 */

import {
  collection,
  doc,
  getDoc,
  getDocFromServer,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  onSnapshot,
  Unsubscribe,
  writeBatch,
  runTransaction
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { Profile, Address, Vehicle, InsurancePolicy, EmergencyContact, UtilityAccount, PilotInfo, HouseholdMember } from '../types/profile';
import { encryptionService } from './encryptionService';

const PROFILES_COLLECTION = 'profiles';
const PILOTS_COLLECTION = 'pilots';
const CURRENT_PROFILE_KEY = 'lucidDock_currentProfileId';
const USER_ID_KEY = 'lucidDock_userId';
const PROFILE_OWNER_ID_KEY = 'lucidDock_profileOwnerId';

/**
 * Helper to recursively remove undefined values from objects
 * Firestore doesn't accept undefined values
 *
 * Uses JSON.parse(JSON.stringify()) as a reliable way to strip undefined,
 * then does a manual pass to handle any edge cases
 */
function removeUndefinedValues(obj: any): any {
  if (obj === null || obj === undefined) {
    return obj;
  }

  // First pass: Use JSON stringify/parse which naturally strips undefined values
  // This is the most reliable way to remove undefined from nested structures
  try {
    const jsonCleaned = JSON.parse(JSON.stringify(obj));

    // Second pass: Manual cleanup to handle any edge cases the JSON approach might miss
    return deepClean(jsonCleaned);
  } catch {
    // If JSON fails (circular refs, etc.), fall back to manual cleaning
    return deepClean(obj);
  }
}

/**
 * Deep clean helper - manually removes undefined values
 */
function deepClean(obj: any): any {
  if (obj === null) return null;
  if (obj === undefined) return undefined;

  // Handle Date objects - convert to ISO string
  if (obj instanceof Date) {
    return obj.toISOString();
  }

  if (Array.isArray(obj)) {
    return obj
      .map(item => deepClean(item))
      .filter(item => item !== undefined);
  }

  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const [key, value] of Object.entries(obj)) {
      const cleanedValue = deepClean(value);
      if (cleanedValue !== undefined) {
        cleaned[key] = cleanedValue;
      }
    }
    return cleaned;
  }

  return obj;
}

/**
 * Strip base64 data: URLs from address documents and photos before Firestore writes.
 * These should have been uploaded to cloud storage — if they weren't, storing them
 * inline in Firestore would blow past the 1MB document limit.
 */
function stripDataUrls(profile: any): any {
  if (!profile?.addresses || !Array.isArray(profile.addresses)) return profile;

  const cleaned = { ...profile };
  cleaned.addresses = cleaned.addresses.map((addr: any) => {
    const a = { ...addr };
    if (Array.isArray(a.documents)) {
      const before = a.documents.length;
      a.documents = a.documents
        .filter((d: any) => !d.url?.startsWith('data:'))
        .map(({ pendingFile, ...rest }: any) => rest);
      if (a.documents.length < before) {
        console.warn(`ProfileService: Stripped ${before - a.documents.length} data-URL document(s) from address "${a.label || a.street}"`);
      }
    }
    if (Array.isArray(a.photos)) {
      const before = a.photos.length;
      a.photos = a.photos
        .filter((p: any) => !p.url?.startsWith('data:'))
        .map(({ pendingFile, ...rest }: any) => rest);
      if (a.photos.length < before) {
        console.warn(`ProfileService: Stripped ${before - a.photos.length} data-URL photo(s) from address "${a.label || a.street}"`);
      }
    }
    return a;
  });
  return cleaned;
}

// =====================
// SECURITY HELPER - DENORMALIZED ARRAYS
// =====================

import { FamilyMember } from '../types/family-sharing';

/**
 * Syncs the denormalized userIds arrays with the members array
 * These arrays are used by Firestore security rules for efficient access checks
 * - memberUserIds: ALL active member userIds (for backward compatibility queries)
 * - editorUserIds: Only Co-Pilots (editor role)
 * - viewerUserIds: Only Passengers (viewer role)
 * - memberEmails: ALL active member emails (for first-time login before userId sync)
 */
export function syncDenormalizedUserIds(members: FamilyMember[]): {
  memberUserIds: string[];
  editorUserIds: string[];
  viewerUserIds: string[];
  memberEmails: string[];
} {
  const memberUserIds: string[] = [];
  const editorUserIds: string[] = [];
  const viewerUserIds: string[] = [];
  const memberEmails: string[] = [];

  for (const member of members) {
    // Use !== false to handle legacy data where isActive might be undefined
    if (member.isActive === false) continue;

    // Add email for ALL active members (enables first-time login with email matching)
    // This is critical for new Co-Pilots signing in with Google for the first time
    if (member.email) {
      memberEmails.push(member.email.toLowerCase());
    }

    // Only add to userId arrays if member has a userId
    if (!member.userId) continue;

    // Add to memberUserIds for all active members (enables backward compat queries)
    memberUserIds.push(member.userId);

    if (member.role === 'editor') {
      editorUserIds.push(member.userId);
    } else if (member.role === 'viewer') {
      viewerUserIds.push(member.userId);
    }
    // Note: 'owner' role is not included in editor/viewer - they use ownerId field
  }

  return { memberUserIds, editorUserIds, viewerUserIds, memberEmails };
}

// =====================
// ENCRYPTION HELPERS
// =====================

// Fields to encrypt in vehicles
const VEHICLE_ENCRYPTED_FIELDS = ['vin', 'licensePlate'] as const;

// Fields to encrypt in addresses
const ADDRESS_ENCRYPTED_FIELDS = ['wifiPassword', 'accessCodes'] as const;

// Fields to encrypt in utility accounts (nested in addresses)
const UTILITY_ENCRYPTED_FIELDS = ['accountNumber'] as const;

// Fields to encrypt at profile level
const PROFILE_ENCRYPTED_FIELDS = ['dateOfBirth'] as const;

// Fields to encrypt in ownerSecurity
const OWNER_SECURITY_ENCRYPTED_FIELDS = ['dateOfBirth', 'recoveryEmail', 'recoveryPhone'] as const;

// Fields to encrypt in family member important dates
const IMPORTANT_DATE_ENCRYPTED_FIELDS = ['date'] as const;

/**
 * Encrypts sensitive fields in a vehicle
 * Always encrypts - the caller is responsible for providing plaintext data
 * NOTE: loadProfile() returns decrypted data, so this will correctly encrypt plaintext
 */
async function encryptVehicle(vehicle: Vehicle, userId: string): Promise<Vehicle> {
  const encrypted: any = { ...vehicle };

  for (const field of VEHICLE_ENCRYPTED_FIELDS) {
    if (encrypted[field]) {
      // Always encrypt - data from loadProfile() is always decrypted
      encrypted[field] = await encryptionService.encrypt(encrypted[field], userId);
    }
  }

  return encrypted as Vehicle;
}

/**
 * Decrypts sensitive fields in a vehicle
 * Uses fallback decryption to handle data encrypted with different keys
 */
async function decryptVehicle(vehicle: Vehicle, primaryUserId: string, fallbackUserId?: string): Promise<Vehicle> {
  const decrypted: any = { ...vehicle };

  for (const field of VEHICLE_ENCRYPTED_FIELDS) {
    if (decrypted[field]) {
      decrypted[field] = await encryptionService.decryptWithFallback(decrypted[field], primaryUserId, fallbackUserId);
    }
  }

  return decrypted as Vehicle;
}

/**
 * Encrypts sensitive fields in a utility account
 * Always encrypts - the caller is responsible for providing plaintext data
 */
async function encryptUtilityAccount(account: UtilityAccount, userId: string): Promise<UtilityAccount> {
  const encrypted: any = { ...account };

  for (const field of UTILITY_ENCRYPTED_FIELDS) {
    if (encrypted[field]) {
      // Always encrypt - data from loadProfile() is always decrypted
      encrypted[field] = await encryptionService.encrypt(encrypted[field], userId);
    }
  }

  return encrypted as UtilityAccount;
}

/**
 * Decrypts sensitive fields in a utility account
 * Uses fallback decryption to handle data encrypted with different keys
 */
async function decryptUtilityAccount(account: UtilityAccount, primaryUserId: string, fallbackUserId?: string): Promise<UtilityAccount> {
  const decrypted: any = { ...account };

  for (const field of UTILITY_ENCRYPTED_FIELDS) {
    if (decrypted[field]) {
      decrypted[field] = await encryptionService.decryptWithFallback(decrypted[field], primaryUserId, fallbackUserId);
    }
  }

  return decrypted as UtilityAccount;
}

/**
 * Encrypts sensitive fields in an address (including nested utility accounts)
 * Always encrypts - the caller is responsible for providing plaintext data
 */
async function encryptAddress(address: Address, userId: string): Promise<Address> {
  const encrypted: any = { ...address };

  // Encrypt address-level fields
  for (const field of ADDRESS_ENCRYPTED_FIELDS) {
    if (encrypted[field]) {
      // Always encrypt - data from loadProfile() is always decrypted
      encrypted[field] = await encryptionService.encrypt(encrypted[field], userId);
    }
  }

  // Encrypt nested utility accounts
  if (encrypted.utilityAccounts && encrypted.utilityAccounts.length > 0) {
    encrypted.utilityAccounts = await Promise.all(
      encrypted.utilityAccounts.map((account: UtilityAccount) => encryptUtilityAccount(account, userId))
    );
  }

  return encrypted as Address;
}

/**
 * Decrypts sensitive fields in an address (including nested utility accounts)
 * Uses fallback decryption to handle data encrypted with different keys
 */
async function decryptAddress(address: Address, primaryUserId: string, fallbackUserId?: string): Promise<Address> {
  const decrypted: any = { ...address };

  // Decrypt address-level fields
  for (const field of ADDRESS_ENCRYPTED_FIELDS) {
    if (decrypted[field]) {
      decrypted[field] = await encryptionService.decryptWithFallback(decrypted[field], primaryUserId, fallbackUserId);
    }
  }

  // Decrypt nested utility accounts
  if (decrypted.utilityAccounts && decrypted.utilityAccounts.length > 0) {
    decrypted.utilityAccounts = await Promise.all(
      decrypted.utilityAccounts.map((account: UtilityAccount) => decryptUtilityAccount(account, primaryUserId, fallbackUserId))
    );
  }

  return decrypted as Address;
}

/**
 * Encrypts sensitive fields in an ImportantDate
 * Always encrypts - the caller is responsible for providing plaintext data
 */
async function encryptImportantDate(date: any, userId: string): Promise<any> {
  const encrypted = { ...date };

  for (const field of IMPORTANT_DATE_ENCRYPTED_FIELDS) {
    if (encrypted[field]) {
      // Always encrypt - data from loadProfile() is always decrypted
      encrypted[field] = await encryptionService.encrypt(encrypted[field], userId);
    }
  }

  return encrypted;
}

/**
 * Decrypts sensitive fields in an ImportantDate
 * Uses fallback decryption to handle data encrypted with different keys
 */
async function decryptImportantDate(date: any, primaryUserId: string, fallbackUserId?: string): Promise<any> {
  const decrypted = { ...date };

  for (const field of IMPORTANT_DATE_ENCRYPTED_FIELDS) {
    if (decrypted[field]) {
      decrypted[field] = await encryptionService.decryptWithFallback(decrypted[field], primaryUserId, fallbackUserId);
    }
  }

  return decrypted;
}

/**
 * Encrypts sensitive fields in OwnerSecurity
 * Always encrypts - the caller is responsible for providing plaintext data
 */
async function encryptOwnerSecurity(security: any, userId: string): Promise<any> {
  if (!security) return security;
  const encrypted = { ...security };

  for (const field of OWNER_SECURITY_ENCRYPTED_FIELDS) {
    if (encrypted[field]) {
      // Always encrypt - data from loadProfile() is always decrypted
      encrypted[field] = await encryptionService.encrypt(encrypted[field], userId);
    }
  }

  return encrypted;
}

/**
 * Decrypts sensitive fields in OwnerSecurity
 * Uses fallback decryption to handle data encrypted with different keys
 */
async function decryptOwnerSecurity(security: any, primaryUserId: string, fallbackUserId?: string): Promise<any> {
  if (!security) return security;
  const decrypted = { ...security };

  for (const field of OWNER_SECURITY_ENCRYPTED_FIELDS) {
    if (decrypted[field]) {
      decrypted[field] = await encryptionService.decryptWithFallback(decrypted[field], primaryUserId, fallbackUserId);
    }
  }

  return decrypted;
}

/**
 * Encrypts sensitive fields in a FamilyMember
 */
async function encryptFamilyMember(member: any, userId: string): Promise<any> {
  const encrypted = { ...member };

  // Encrypt importantDates if present
  if (encrypted.importantDates && encrypted.importantDates.length > 0) {
    encrypted.importantDates = await Promise.all(
      encrypted.importantDates.map((date: any) => encryptImportantDate(date, userId))
    );
  }

  return encrypted;
}

/**
 * Decrypts sensitive fields in a FamilyMember
 * Uses fallback decryption to handle data encrypted with different keys
 */
async function decryptFamilyMember(member: any, primaryUserId: string, fallbackUserId?: string): Promise<any> {
  const decrypted = { ...member };

  // Decrypt importantDates if present
  if (decrypted.importantDates && decrypted.importantDates.length > 0) {
    decrypted.importantDates = await Promise.all(
      decrypted.importantDates.map((date: any) => decryptImportantDate(date, primaryUserId, fallbackUserId))
    );
  }

  return decrypted;
}

/**
 * Encrypts all sensitive fields in a profile before saving
 * Always encrypts - the caller is responsible for providing plaintext data (from loadProfile)
 */
async function encryptProfile(profile: Profile, userId: string): Promise<Profile> {
  const encrypted: any = { ...profile };

  // Encrypt profile-level fields (dateOfBirth)
  for (const field of PROFILE_ENCRYPTED_FIELDS) {
    if (encrypted[field]) {
      // Always encrypt - data from loadProfile() is always decrypted
      encrypted[field] = await encryptionService.encrypt(encrypted[field], userId);
    }
  }

  // Encrypt vehicles
  if (encrypted.vehicles && encrypted.vehicles.length > 0) {
    encrypted.vehicles = await Promise.all(
      encrypted.vehicles.map((vehicle: Vehicle) => encryptVehicle(vehicle, userId))
    );
  }

  // Encrypt addresses
  if (encrypted.addresses && encrypted.addresses.length > 0) {
    encrypted.addresses = await Promise.all(
      encrypted.addresses.map((address: Address) => encryptAddress(address, userId))
    );
  }

  // Encrypt ownerSecurity
  if (encrypted.ownerSecurity) {
    encrypted.ownerSecurity = await encryptOwnerSecurity(encrypted.ownerSecurity, userId);
  }

  // Encrypt family members' importantDates
  if (encrypted.members && encrypted.members.length > 0) {
    encrypted.members = await Promise.all(
      encrypted.members.map((member: any) => encryptFamilyMember(member, userId))
    );
  }

  return encrypted as Profile;
}

/**
 * Decrypts all sensitive fields in a profile after loading
 * Uses fallback decryption to handle data encrypted with different keys
 * @param profile - The profile to decrypt
 * @param primaryUserId - Primary user ID for decryption (usually profile.ownerId)
 * @param fallbackUserId - Optional fallback user ID (usually current user) if primary fails
 */
async function decryptProfile(profile: Profile, primaryUserId: string, fallbackUserId?: string): Promise<Profile> {
  const decrypted: any = { ...profile };
  // Get the current user ID as fallback if not provided
  const fallback = fallbackUserId || ProfileService.getCurrentUserId() || undefined;

  // Decrypt profile-level fields (dateOfBirth)
  for (const field of PROFILE_ENCRYPTED_FIELDS) {
    if (decrypted[field]) {
      decrypted[field] = await encryptionService.decryptWithFallback(decrypted[field], primaryUserId, fallback);
    }
  }

  // Decrypt vehicles
  if (decrypted.vehicles && decrypted.vehicles.length > 0) {
    decrypted.vehicles = await Promise.all(
      decrypted.vehicles.map((vehicle: Vehicle) => decryptVehicle(vehicle, primaryUserId, fallback))
    );
  }

  // Decrypt addresses
  if (decrypted.addresses && decrypted.addresses.length > 0) {
    decrypted.addresses = await Promise.all(
      decrypted.addresses.map((address: Address) => decryptAddress(address, primaryUserId, fallback))
    );
  }

  // Decrypt ownerSecurity
  if (decrypted.ownerSecurity) {
    decrypted.ownerSecurity = await decryptOwnerSecurity(decrypted.ownerSecurity, primaryUserId, fallback);
  }

  // Decrypt family members' importantDates
  if (decrypted.members && decrypted.members.length > 0) {
    decrypted.members = await Promise.all(
      decrypted.members.map((member: any) => decryptFamilyMember(member, primaryUserId, fallback))
    );
  }

  return decrypted as Profile;
}

// PilotInfo is imported from ../types/profile

export class ProfileService {
  static generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  /**
   * Get current user ID from localStorage (session data)
   */
  static getCurrentUserId(): string | null {
    return localStorage.getItem(USER_ID_KEY);
  }

  /**
   * Set current user ID in localStorage (session data)
   */
  static setCurrentUserId(userId: string): void {
    localStorage.setItem(USER_ID_KEY, userId);
  }

  /**
   * Get current profile ID from localStorage (session data)
   */
  static getCurrentProfileId(): string | null {
    return localStorage.getItem(CURRENT_PROFILE_KEY);
  }

  /**
   * Set current profile ID in localStorage (session data)
   */
  static setCurrentProfileId(profileId: string): void {
    localStorage.setItem(CURRENT_PROFILE_KEY, profileId);
  }

  /**
   * Get current profile owner ID from localStorage
   * Used for encryption key derivation
   */
  static getProfileOwnerId(): string | null {
    return localStorage.getItem(PROFILE_OWNER_ID_KEY);
  }

  /**
   * Set current profile owner ID in localStorage
   * Should be called when switching profiles
   */
  static setProfileOwnerId(ownerId: string): void {
    localStorage.setItem(PROFILE_OWNER_ID_KEY, ownerId);
  }

  /**
   * Get the encryption user ID - uses profile owner ID for consistency
   * Falls back to current user ID for backwards compatibility
   */
  static getEncryptionUserId(): string {
    // First try profile owner ID (for decrypting data owned by that profile)
    const profileOwnerId = ProfileService.getProfileOwnerId();
    if (profileOwnerId) {
      return profileOwnerId;
    }
    // Fallback to current user ID
    return ProfileService.getCurrentUserId() || '';
  }

  /**
   * Update fields on the pilot document (pilots collection).
   * Used for Stripe subscription data and other pilot-level fields.
   */
  static async updatePilotInfo(pilotId: string, data: Record<string, unknown>): Promise<void> {
    const pilotRef = doc(db, PILOTS_COLLECTION, pilotId);
    await updateDoc(pilotRef, data);
  }

  /**
   * Get Pilot info from the pilots collection
   * This is the source of truth for the Pilot's name (from Google Sign-in)
   * @param pilotId - The ownerId from the profile (same as pilot's Firebase Auth UID)
   */
  static async getPilotInfo(pilotId: string): Promise<PilotInfo | null> {
    try {
      if (!pilotId) return null;
      const currentUser = auth.currentUser;
      // Only the pilot can read their own pilot doc (per rules)
      if (!currentUser || currentUser.uid !== pilotId) {
        return null;
      }
      const pilotRef = doc(db, PILOTS_COLLECTION, pilotId);

      let pilotDoc;
      try {
        pilotDoc = await getDocFromServer(pilotRef);
      } catch {
        // Fallback to getDoc if server read fails (e.g. assertion errors)
        pilotDoc = await getDoc(pilotRef);
      }

      if (pilotDoc.exists()) {
        const data = pilotDoc.data();
        const firstName = data.firstName || '';
        const lastName = data.lastName || '';
        const fullName = `${firstName}${lastName ? ' ' + lastName : ''}`.trim();

        const pilotInfo = {
          firstName,
          lastName,
          email: data.email,
          photoURL: data.photoURL,
          fullName: fullName || 'Owner',
          membershipPlan: data.membershipPlan || 'free',
          stripeCustomerId: data.stripeCustomerId,
          stripeSubscriptionId: data.stripeSubscriptionId,
          subscriptionPeriodStart: data.subscriptionPeriodStart,
          subscriptionPeriodEnd: data.subscriptionPeriodEnd,
          lastActivityAt: data.lastActivityAt?.toMillis?.() || data.lastActivityAt || undefined,
          accountType: data.accountType || undefined,
          agentProfile: data.agentProfile || undefined,
          recommendations: data.recommendations || undefined,
        };
        return pilotInfo;
      }
      return null;
    } catch (error) {
      // If all Firestore reads fail, try to use cached plan from localStorage
      // This prevents Pro users from being incorrectly treated as free users
      const cachedPlan = localStorage.getItem('lucidDock_membershipPlan');
      if (cachedPlan) {
        return {
          firstName: '',
          lastName: '',
          email: '',
          photoURL: '',
          fullName: 'Owner',
          membershipPlan: cachedPlan,
        } as PilotInfo;
      }
      return null;
    }
  }

  /**
   * Save profile to Firestore
   */
  static async saveProfile(profile: Profile): Promise<void> {
    const profileRef = doc(db, PROFILES_COLLECTION, profile.id);
    // IMPORTANT: Always use profile.ownerId for encryption to ensure consistent key
    // This ensures data can be decrypted by anyone with access to the profile
    const encryptionKey = profile.ownerId || ProfileService.getCurrentUserId() || '';

    // Strip any base64 data: URLs that would blow past Firestore's 1MB limit
    const safeProfile = stripDataUrls(profile);

    // Encrypt sensitive fields before saving
    const encryptedProfile = await encryptProfile(safeProfile, encryptionKey);

    // Remove undefined values before saving to Firestore
    // Uses JSON.parse(JSON.stringify()) which reliably strips undefined
    const cleanProfile = removeUndefinedValues({
      ...encryptedProfile,
      updatedAt: new Date().toISOString(),
    });
    try {
      await setDoc(profileRef, cleanProfile);
    } catch (error: any) {
      if (error?.message?.includes('exceeds the maximum allowed size')) {
        throw new Error(
          'Your profile has too much data to save. Try removing some photos or documents to free up space.',
        );
      }
      throw error;
    }
  }

  /**
   * Load all profiles for current user from Firestore
   * Now loads ALL profiles where user is a member (owner, editor, or viewer)
   */
  static async loadProfiles(userId?: string): Promise<Profile[]> {
    const resolvedUserId = userId || ProfileService.getCurrentUserId();
    if (!resolvedUserId) {
      console.warn('No user ID found, returning empty profiles');
      return [];
    }

    // Use getAllProfilesForUser to get all profiles where user is a member
    return ProfileService.getAllProfilesForUser(resolvedUserId);
  }

  /**
   * Get profiles owned by a specific user ID
   * Cleans any undefined values from the returned data
   */
  static async getUserProfiles(userId: string): Promise<Profile[]> {
    if (!userId) {
      return [];
    }

    const profilesRef = collection(db, PROFILES_COLLECTION);
    const q = query(profilesRef, where('ownerId', '==', userId));
    const querySnapshot = await getDocs(q);

    const profiles: Profile[] = [];
    querySnapshot.forEach((docSnap) => {
      // Clean the data to remove any undefined values that may have been stored
      const rawData = docSnap.data();
      const cleanedData = removeUndefinedValues({ id: docSnap.id, ...rawData });
      profiles.push(cleanedData as Profile);
    });

    // Decrypt sensitive fields in all profiles
    // Use profile.ownerId for decryption to ensure correct key is used
    const decryptedProfiles = await Promise.all(
      profiles.map(profile => decryptProfile(profile, profile.ownerId || userId))
    );

    return decryptedProfiles;
  }

  /**
   * Get ALL profiles where the user is a member (any role: owner, editor, viewer)
   * This enables multi-profile support where a user can be:
   * - Pilot on their own profile
   * - Co-Pilot or Passenger on other family profiles
   *
   * Uses 3 separate queries to work with Firestore security rules:
   * 1. Profiles where user is owner (ownerId == userId)
   * 2. Profiles where user is Co-Pilot (userId in editorUserIds)
   * 3. Profiles where user is Passenger (userId in viewerUserIds)
   */
  static async getAllProfilesForUser(userId: string): Promise<Profile[]> {
    if (!userId) {
      return [];
    }

    const profilesRef = collection(db, PROFILES_COLLECTION);
    const profilesMap = new Map<string, Profile>();

    // Query 1: Profiles where user is owner
    const ownerQuery = query(profilesRef, where('ownerId', '==', userId));
    const ownerSnapshot = await getDocs(ownerQuery);
    for (const docSnap of ownerSnapshot.docs) {
      const rawData = docSnap.data();
      const cleanedData = removeUndefinedValues({ id: docSnap.id, ...rawData });
      const profile = cleanedData as Profile;

      // Auto-sync: Always sync denormalized arrays when Pilot loads their profile
      // This ensures Co-Pilots and Passengers can log in even if arrays were outdated
      const { memberUserIds, editorUserIds, viewerUserIds, memberEmails } = syncDenormalizedUserIds(profile.members || []);

      // Check if sync is needed (arrays missing, empty, or out of sync)
      const currentMemberIds = profile.memberUserIds || [];
      const currentEditorIds = profile.editorUserIds || [];
      const currentViewerIds = profile.viewerUserIds || [];
      const currentMemberEmails = profile.memberEmails || [];

      const needsSync =
        profile.memberUserIds === undefined ||
        profile.editorUserIds === undefined ||
        profile.viewerUserIds === undefined ||
        profile.memberEmails === undefined ||
        memberUserIds.length !== currentMemberIds.length ||
        editorUserIds.length !== currentEditorIds.length ||
        viewerUserIds.length !== currentViewerIds.length ||
        memberEmails.length !== currentMemberEmails.length ||
        !memberUserIds.every(id => currentMemberIds.includes(id)) ||
        !editorUserIds.every(id => currentEditorIds.includes(id)) ||
        !viewerUserIds.every(id => currentViewerIds.includes(id)) ||
        !memberEmails.every((email: string) => currentMemberEmails.includes(email));

      if (needsSync) {
        try {
          await updateDoc(doc(db, PROFILES_COLLECTION, docSnap.id), {
            memberUserIds,
            editorUserIds,
            viewerUserIds,
            memberEmails
          });
          profile.memberUserIds = memberUserIds;
          profile.editorUserIds = editorUserIds;
          profile.viewerUserIds = viewerUserIds;
        } catch (error) {
          console.error(`Failed to auto-sync denormalized arrays for profile ${docSnap.id}:`, error);
          // Still add the arrays to the in-memory profile even if Firestore update fails
          profile.memberUserIds = memberUserIds;
          profile.editorUserIds = editorUserIds;
          profile.viewerUserIds = viewerUserIds;
        }
      } else {
        // Even if no sync needed, ensure arrays are set on profile object
        profile.memberUserIds = memberUserIds;
        profile.editorUserIds = editorUserIds;
        profile.viewerUserIds = viewerUserIds;
      }

      profilesMap.set(docSnap.id, profile);
    }

    // Query 2: Profiles where user is Co-Pilot (editor)
    const editorQuery = query(profilesRef, where('editorUserIds', 'array-contains', userId));
    const editorSnapshot = await getDocs(editorQuery);
    editorSnapshot.forEach((docSnap) => {
      if (!profilesMap.has(docSnap.id)) {
        const rawData = docSnap.data();
        const cleanedData = removeUndefinedValues({ id: docSnap.id, ...rawData });
        profilesMap.set(docSnap.id, cleanedData as Profile);
      }
    });

    // Query 3: Profiles where user is Passenger (viewer)
    const viewerQuery = query(profilesRef, where('viewerUserIds', 'array-contains', userId));
    const viewerSnapshot = await getDocs(viewerQuery);
    viewerSnapshot.forEach((docSnap) => {
      if (!profilesMap.has(docSnap.id)) {
        const rawData = docSnap.data();
        const cleanedData = removeUndefinedValues({ id: docSnap.id, ...rawData });
        profilesMap.set(docSnap.id, cleanedData as Profile);
      }
    });

    // Query 4: Fallback - Profiles where user's email is in memberEmails array
    // This handles cases where editorUserIds/viewerUserIds aren't synced yet
    const userEmail = localStorage.getItem('userEmail')?.toLowerCase();
    if (userEmail) {
      const emailQuery = query(profilesRef, where('memberEmails', 'array-contains', userEmail));
      const emailSnapshot = await getDocs(emailQuery);
      for (const docSnap of emailSnapshot.docs) {
        if (!profilesMap.has(docSnap.id)) {
          const rawData = docSnap.data();
          const cleanedData = removeUndefinedValues({ id: docSnap.id, ...rawData });
          const profile = cleanedData as Profile;

          // Auto-sync: Update the denormalized arrays since we found via email
          // This helps fix profiles where userId arrays weren't synced
          const { editorUserIds, viewerUserIds } = syncDenormalizedUserIds(profile.members || []);
          const needsSync = !editorUserIds.includes(userId) && !viewerUserIds.includes(userId);

          if (needsSync) {
            // Find the member by email and update their userId if needed
            const memberByEmail = profile.members?.find(m => m.email?.toLowerCase() === userEmail);
            if (memberByEmail && (!memberByEmail.userId || memberByEmail.userId !== userId)) {
              // Update member's userId in the database
              const updatedMembers = profile.members?.map(m => {
                if (m.email?.toLowerCase() === userEmail) {
                  return { ...m, userId: userId };
                }
                return m;
              });
              if (updatedMembers) {
                const syncedArrays = syncDenormalizedUserIds(updatedMembers);
                try {
                  await updateDoc(doc(db, PROFILES_COLLECTION, docSnap.id), {
                    members: updatedMembers,
                    memberUserIds: syncedArrays.memberUserIds,
                    editorUserIds: syncedArrays.editorUserIds,
                    viewerUserIds: syncedArrays.viewerUserIds,
                    memberEmails: syncedArrays.memberEmails
                  });
                  profile.members = updatedMembers;
                } catch (syncError) {
                  // Permission denied is expected when user is a Passenger (viewer) —
                  // only the owner or editors can update the profile document.
                  // Still use the in-memory updated members for this session.
                  console.warn(`[ProfileService] Auto-sync members for profile ${docSnap.id} failed (expected for viewers):`, syncError);
                  profile.members = updatedMembers;
                }
              }
            }
          }

          profilesMap.set(docSnap.id, profile);
        }
      }
    }

    const profiles = Array.from(profilesMap.values());

    // Decrypt sensitive fields in all profiles
    // Use profile.ownerId for decryption to ensure correct key is used
    const decryptedProfiles = await Promise.all(
      profiles.map(profile => decryptProfile(profile, profile.ownerId || userId))
    );

    // Sort: Profiles with more data come first
    // For family members, the shared profile (pilot's) usually has the data
    // For owners, their own profile has the data
    decryptedProfiles.sort((a, b) => {
      const aIsOwner = a.ownerId === userId;
      const bIsOwner = b.ownerId === userId;

      // Count data items in each profile (more data = more useful profile)
      const aDataCount = (a.addresses?.length || 0) + (a.vehicles?.length || 0) +
                         (a.insurancePolicies?.length || 0) + (a.members?.length || 0);
      const bDataCount = (b.addresses?.length || 0) + (b.vehicles?.length || 0) +
                         (b.insurancePolicies?.length || 0) + (b.members?.length || 0);

      // Profile with more data comes first
      if (aDataCount !== bDataCount) {
        return bDataCount - aDataCount;
      }

      // If equal data, owned profiles come first
      if (aIsOwner && !bIsOwner) return -1;
      if (!aIsOwner && bIsOwner) return 1;

      return (a.name || '').localeCompare(b.name || '');
    });

    return decryptedProfiles;
  }

  /**
   * Load a single profile by ID from Firestore
   * Cleans any undefined values that may have been stored previously
   */
  static async loadProfile(profileId: string): Promise<Profile | null> {
    const profileRef = doc(db, PROFILES_COLLECTION, profileId);
    let profileSnap;
    try {
      // Prefer server source to avoid cache state issues
      profileSnap = await getDocFromServer(profileRef);
    } catch (serverError) {
      // If the error is a Firestore internal assertion, log and try getDoc as fallback
      const errMsg = (serverError as Error)?.message || '';
      if (errMsg.includes('INTERNAL ASSERTION FAILED') || errMsg.includes('Unexpected state')) {
        console.warn('ProfileService.loadProfile: Firestore assertion error on getDocFromServer, trying getDoc fallback');
      }
      try {
        profileSnap = await getDoc(profileRef);
      } catch (fallbackError) {
        // Both server and cache reads failed - return null instead of crashing
        console.error('ProfileService.loadProfile: Both getDocFromServer and getDoc failed:', fallbackError);
        return null;
      }
    }

    if (profileSnap.exists()) {
      // Clean the data to remove any undefined values that may have been stored
      const rawData = profileSnap.data();
      const cleanedData = removeUndefinedValues({ id: profileSnap.id, ...rawData });
      const profile = cleanedData as Profile;

      // Decrypt sensitive fields using profile.ownerId for consistent key
      // This ensures data encrypted by owner can be decrypted by anyone with access
      return decryptProfile(profile, profile.ownerId || '');
    }

    return null;
  }

  /**
   * Find a profile by invitation ID
   * Cleans any undefined values from the returned data
   */
  static async findProfileByInvitationId(invitationId: string): Promise<{ profile: Profile; invitation: any } | null> {
    try {
      const profilesRef = collection(db, PROFILES_COLLECTION);
      const querySnapshot = await getDocs(profilesRef);

      for (const docSnap of querySnapshot.docs) {
        // Clean the data to remove any undefined values that may have been stored
        const rawData = docSnap.data();
        const cleanedData = removeUndefinedValues({ id: docSnap.id, ...rawData });
        const profile = cleanedData as Profile;
        const invitation = profile.invitations?.find(inv => inv.id === invitationId);

        if (invitation) {
          return { profile, invitation };
        }
      }

      return null;
    } catch (error) {
      console.error('ProfileService: Error in findProfileByInvitationId:', error);
      throw error;
    }
  }

  /**
   * Track invitation view - Update viewCount, firstViewedAt, lastViewedAt
   */
  static async trackInvitationView(profileId: string, invitationId: string): Promise<void> {
    const profile = await ProfileService.loadProfile(profileId);
    if (!profile) {
      throw new Error('Profile not found');
    }

    const now = new Date().toISOString();
    const updatedInvitations = profile.invitations?.map(inv => {
      if (inv.id === invitationId) {
        return {
          ...inv,
          viewCount: (inv.viewCount || 0) + 1,
          firstViewedAt: inv.firstViewedAt || now,
          lastViewedAt: now,
        };
      }
      return inv;
    });

    await ProfileService.updateProfile(profileId, { invitations: updatedInvitations });
  }

  /**
   * Update invitation status (e.g., from 'pending' to 'accepted')
   */
  static async updateInvitationStatus(
    profileId: string,
    invitationId: string,
    status: 'pending' | 'accepted' | 'expired' | 'declined'
  ): Promise<void> {
    const profile = await ProfileService.loadProfile(profileId);
    if (!profile) {
      throw new Error('Profile not found');
    }

    const now = new Date().toISOString();
    const updatedInvitations = profile.invitations?.map(inv => {
      if (inv.id === invitationId) {
        return {
          ...inv,
          status,
          acceptedAt: status === 'accepted' ? now : inv.acceptedAt,
        };
      }
      return inv;
    });

    await ProfileService.updateProfile(profileId, { invitations: updatedInvitations });
  }

  /**
   * Add family member to profile from accepted invitation
   * Creates a separate profile for the family member with access to pilot's shared data
   */
  static async addFamilyMemberFromInvitation(
    profileId: string,
    invitationId: string
  ): Promise<string> {
    const pilotProfile = await ProfileService.loadProfile(profileId);
    if (!pilotProfile) {
      throw new Error('Profile not found');
    }

    // Find the accepted invitation
    const invitation = pilotProfile.invitations?.find(inv => inv.id === invitationId && inv.status === 'accepted');
    if (!invitation) {
      throw new Error('Accepted invitation not found');
    }

    // Check if member already exists in pilot's profile (by email or by name+role)
    const existingMember = pilotProfile.members?.find(m =>
      (m.email && m.email === invitation.email) ||
      (m.displayName === invitation.displayName && m.role === invitation.role)
    );

    if (existingMember) {
      // Check if member already has their own profile
      const existingMemberProfile = await ProfileService.getUserProfiles(existingMember.userId);
      if (existingMemberProfile && existingMemberProfile.length > 0) {
        return existingMemberProfile[0].id;
      }
    }

    // Generate unique userId for the family member
    const memberUserId = existingMember?.userId || ProfileService.generateId();

    // Create new family member entry in pilot's profile
    // Only include fields that are not undefined to avoid Firestore errors
    const fullName = invitation.lastName
      ? `${invitation.displayName} ${invitation.lastName}`
      : invitation.displayName;

    const now = new Date().toISOString();
    const newMember: any = {
      userId: memberUserId,
      displayName: fullName,
      role: invitation.role || 'viewer', // Default to 'viewer' if undefined
      addedBy: pilotProfile.ownerId,
      addedAt: now,
      isActive: true,
      lastAccessedAt: now, // Set when member first signs in via family code
    };

    // Only add optional fields if they're defined
    if (invitation.relationship) newMember.relationship = invitation.relationship;
    if (invitation.customRelationship) newMember.customRelationship = invitation.customRelationship;
    // Use invitation.email (required field for authentication)
    if (invitation.email) newMember.email = invitation.email;
    // Add phone number if provided in invitation
    if (invitation.phone) {
      newMember.phone = invitation.phone;
    } else if (invitation.inviteMethod === 'sms' && invitation.contact) {
      // Fallback: Add phone from contact if invite method was SMS
      newMember.phone = invitation.contact;
    }
    // Add member to pilot's profile if not already there
    if (!existingMember) {
      // Clean existing members to remove any undefined values from previously saved data
      const cleanedExistingMembers = (pilotProfile.members || []).map((m: any) => {
        const cleaned: any = {};
        for (const [key, value] of Object.entries(m)) {
          if (value !== undefined) {
            cleaned[key] = value;
          }
        }
        return cleaned;
      });
      const updatedMembers = [...cleanedExistingMembers, newMember];
      await ProfileService.updateProfile(profileId, { members: updatedMembers });
    } else {
      // Member already exists - update lastAccessedAt and ensure denormalized arrays are in sync
      const updatedMembers = (pilotProfile.members || []).map((m: any) => {
        if (m.userId === existingMember.userId || m.email?.toLowerCase() === invitation.email?.toLowerCase()) {
          return { ...m, lastAccessedAt: new Date().toISOString() };
        }
        return m;
      });

      // Always update to set lastAccessedAt (also syncs denormalized arrays via updateProfile)
      await ProfileService.updateProfile(profileId, { members: updatedMembers });
    }

    // Create a separate profile for the family member
    const memberProfileName = invitation.lastName
      ? `${invitation.displayName} ${invitation.lastName}`
      : invitation.displayName;

    const memberProfile = await ProfileService.createProfile(memberProfileName, memberUserId);

    // Add pilot's profile as a shared profile for the member
    // Store reference to pilot's profile so member can access shared data
    await ProfileService.updateProfile(memberProfile.id, {
      sharedProfiles: [{
        profileId: pilotProfile.id,
        profileName: pilotProfile.name,
        accessLevel: invitation.role || 'viewer', // 'editor' or 'viewer', default to 'viewer' if undefined
        sharedBy: pilotProfile.ownerId,
        sharedAt: new Date().toISOString()
      }]
    });

    return memberProfile.id;
  }

  /**
   * Subscribe to real-time profile updates
   * Cleans any undefined values from the returned data
   */
  static subscribeToProfile(profileId: string, callback: (profile: Profile | null) => void): Unsubscribe {
    const profileRef = doc(db, PROFILES_COLLECTION, profileId);
    return onSnapshot(profileRef, async (docSnap) => {
      if (docSnap.exists()) {
        // Clean the data to remove any undefined values that may have been stored
        const rawData = docSnap.data();
        const cleanedData = removeUndefinedValues({ id: docSnap.id, ...rawData });
        const profile = cleanedData as Profile;

        // Decrypt sensitive fields using profile.ownerId for consistent key
        const decryptedProfile = await decryptProfile(profile, profile.ownerId || '');
        callback(decryptedProfile);
      } else {
        callback(null);
      }
    });
  }

  /**
   * Subscribe to real-time updates for all profiles where user is a member
   * This includes owned profiles AND shared profiles (as Co-Pilot or Passenger)
   *
   * Uses 3 separate listeners to work with Firestore security rules:
   * 1. Profiles where user is owner (ownerId == userId)
   * 2. Profiles where user is Co-Pilot (userId in editorUserIds)
   * 3. Profiles where user is Passenger (userId in viewerUserIds)
   */
  static subscribeToProfiles(userId: string, callback: (profiles: Profile[]) => void): Unsubscribe {
    const profilesRef = collection(db, PROFILES_COLLECTION);
    const profilesMap = new Map<string, Profile>();
    let pendingUpdates = 0;
    let retryCount = 0;
    const MAX_RETRIES = 3;
    let unsubscribeFunctions: (() => void)[] = [];
    let isUnsubscribed = false;
    let retryTimeoutId: ReturnType<typeof setTimeout> | null = null;

    // Helper to process updates and call callback
    const processUpdates = async () => {
      if (isUnsubscribed) return;

      const profiles = Array.from(profilesMap.values());

      // Decrypt sensitive fields in all profiles
      const decryptedProfiles = await Promise.all(
        profiles.map(profile => decryptProfile(profile, profile.ownerId || userId))
      );

      // Sort: Profiles with more data come first
      decryptedProfiles.sort((a, b) => {
        const aIsOwner = a.ownerId === userId;
        const bIsOwner = b.ownerId === userId;

        const aDataCount = (a.addresses?.length || 0) + (a.vehicles?.length || 0) +
                           (a.insurancePolicies?.length || 0) + (a.members?.length || 0);
        const bDataCount = (b.addresses?.length || 0) + (b.vehicles?.length || 0) +
                           (b.insurancePolicies?.length || 0) + (b.members?.length || 0);

        if (aDataCount !== bDataCount) return bDataCount - aDataCount;
        if (aIsOwner && !bIsOwner) return -1;
        if (!aIsOwner && bIsOwner) return 1;
        return (a.name || '').localeCompare(b.name || '');
      });

      callback(decryptedProfiles);
    };

    // Helper to handle snapshot updates
    const handleSnapshot = (querySnapshot: any) => {
      if (isUnsubscribed) return;

      querySnapshot.docChanges().forEach((change: any) => {
        const docSnap = change.doc;
        if (change.type === 'removed') {
          profilesMap.delete(docSnap.id);
        } else {
          const rawData = docSnap.data();
          const cleanedData = removeUndefinedValues({ id: docSnap.id, ...rawData });
          profilesMap.set(docSnap.id, cleanedData as Profile);
        }
      });
      pendingUpdates--;
      if (pendingUpdates <= 0) {
        processUpdates();
      }
    };

    // Helper to handle errors with retry logic
    const handleListenerError = (error: any, listenerName: string) => {
      if (isUnsubscribed) return;

      const errorMessage = error?.message || '';
      const isInternalError = errorMessage.includes('INTERNAL ASSERTION FAILED') ||
                              errorMessage.includes('Unexpected state');

      if (isInternalError) {
        // Log as warning, not error - these are handled gracefully
        console.warn(`ProfileService: ${listenerName} listener assertion error (retry ${retryCount + 1}/${MAX_RETRIES})`);
      } else {
        console.warn(`ProfileService: ${listenerName} listener error:`, errorMessage);
      }

      if (isInternalError && retryCount < MAX_RETRIES) {
        retryCount++;

        // Unsubscribe from all current listeners
        unsubscribeFunctions.forEach(unsub => {
          try { unsub(); } catch { /* ignore */ }
        });
        unsubscribeFunctions = [];

        // Wait before retrying with exponential backoff
        retryTimeoutId = setTimeout(() => {
          if (!isUnsubscribed) {
            pendingUpdates = 0;
            setupListeners();
          }
        }, 1000 * retryCount);
      } else {
        // For other errors or max retries reached, deliver whatever data we have
        pendingUpdates--;
        if (pendingUpdates <= 0) {
          processUpdates();
        }
      }
    };

    // Set up all 3 listeners
    const setupListeners = () => {
      if (isUnsubscribed) return;

      pendingUpdates = 3;

      // Listener 1: Profiles where user is owner
      const ownerQuery = query(profilesRef, where('ownerId', '==', userId));
      const unsubOwner = onSnapshot(ownerQuery, handleSnapshot, (error) => {
        handleListenerError(error, 'owner');
      });
      unsubscribeFunctions.push(unsubOwner);

      // Listener 2: Profiles where user is Co-Pilot (editor)
      const editorQuery = query(profilesRef, where('editorUserIds', 'array-contains', userId));
      const unsubEditor = onSnapshot(editorQuery, handleSnapshot, (error) => {
        handleListenerError(error, 'editor');
      });
      unsubscribeFunctions.push(unsubEditor);

      // Listener 3: Profiles where user is Passenger (viewer)
      const viewerQuery = query(profilesRef, where('viewerUserIds', 'array-contains', userId));
      const unsubViewer = onSnapshot(viewerQuery, handleSnapshot, (error) => {
        handleListenerError(error, 'viewer');
      });
      unsubscribeFunctions.push(unsubViewer);
    };

    // Initial setup
    setupListeners();

    // Return combined unsubscribe function
    return () => {
      isUnsubscribed = true;
      if (retryTimeoutId) {
        clearTimeout(retryTimeoutId);
      }
      unsubscribeFunctions.forEach(unsub => {
        try { unsub(); } catch { /* ignore */ }
      });
    };
  }

  /**
   * Create default profile for new users
   */
  static createDefaultProfile(userId: string, profileType?: 'personal' | 'business'): Profile {
    const now = new Date().toISOString();

    // Get user's name and email from localStorage
    const firstName = localStorage.getItem('firstName') || '';
    const lastName = localStorage.getItem('lastName') || '';
    const email = localStorage.getItem('userEmail') || '';
    const accountType = profileType || localStorage.getItem('lucidDock_accountType') as 'personal' | 'business' || 'personal';
    const fullName = `${firstName} ${lastName}`.trim() || 'My Profile';
    const displayName = firstName || 'Me';

    // Build member object without undefined values
    const ownerMember: any = {
      userId: userId,
      displayName: displayName,
      role: 'owner',
      addedBy: userId,
      addedAt: now,
      isActive: true,
    };
    // Only add email if it's a non-empty string
    if (email) {
      ownerMember.email = email;
    }

    // Sync denormalized arrays for security rules
    const { memberUserIds, editorUserIds, viewerUserIds, memberEmails } = syncDenormalizedUserIds([ownerMember]);

    const defaultProfile: any = {
      id: ProfileService.generateId(),
      name: fullName,
      ownerId: userId,
      type: accountType,
      addresses: [],
      vehicles: [],
      insurancePolicies: [],
      emergencyContacts: [],
      members: [ownerMember],
      invitations: [],
      isPremium: false,
      memberUserIds,
      editorUserIds,
      viewerUserIds,
      memberEmails,
      createdAt: now,
      updatedAt: now
    };

    return defaultProfile;
  }

  /**
   * Initialize profiles for user (create default if none exist)
   */
  static async initializeProfiles(userId: string): Promise<Profile[]> {
    if (!userId) return [];
    let profiles = await ProfileService.loadProfiles(userId);

    // Check if user is a Pilot - if so, they MUST have a profile where they are the owner
    // This handles the case where a user was a family member first, then became a Pilot
    const userType = localStorage.getItem('lucidDock_userType');
    const hasOwnedProfile = profiles.some(p => p.ownerId === userId);

    if (profiles.length === 0 || (userType === 'pilot' && !hasOwnedProfile)) {
      const defaultProfile = ProfileService.createDefaultProfile(userId);

      // Load pilot's plan and storage settings from Firestore
      try {
        const currentUser = auth.currentUser;
        // Only fetch pilot doc when auth user matches pilot
        if (currentUser && currentUser.uid === userId) {
          const pilotDoc = await getDocFromServer(doc(db, 'pilots', userId));
          if (pilotDoc.exists()) {
            const pilotData = pilotDoc.data();
            // Set membership plan from pilot document
            if (pilotData.membershipPlan) {
              defaultProfile.membershipPlan = pilotData.membershipPlan;
              // Set isPremium flag based on plan
              defaultProfile.isPremium = pilotData.membershipPlan === 'premium';
            }
            // Copy storage settings from pilot document
            if (pilotData.storageSettings) {
              defaultProfile.storageSettings = pilotData.storageSettings;
            }
          }
        }
      } catch {
        // Ignore pilot plan settings if not accessible
      }

      await ProfileService.saveProfile(defaultProfile);
      profiles = [...profiles, defaultProfile];
      ProfileService.setCurrentProfileId(defaultProfile.id);
    }

    return profiles;
  }

  /**
   * Get all profiles for a specific user (by ownerId)
   * Used to find existing profiles for family members during re-authentication
   */
  static async getProfilesForUser(userId: string): Promise<Profile[]> {
    try {
      const profilesRef = collection(db, PROFILES_COLLECTION);
      const q = query(profilesRef, where('ownerId', '==', userId));
      const querySnapshot = await getDocs(q);

      const profiles: Profile[] = [];
      querySnapshot.forEach((doc) => {
        profiles.push({ id: doc.id, ...doc.data() } as Profile);
      });

      return profiles;
    } catch (error) {
      console.error('Error getting profiles for user:', error);
      return [];
    }
  }

  /**
   * Create a profile for a family member who is already active in a pilot's profile
   * Used when member exists in members array but doesn't have their own profile yet
   */
  static async createProfileForFamilyMember(
    _pilotProfileId: string,
    member: any
  ): Promise<string> {
    const now = new Date().toISOString();

    // Create a profile ID for the family member (use their existing userId)
    const memberProfileId = `family_${member.userId}_${Date.now()}`;

    // Create a minimal profile for the family member
    const memberData = {
      userId: member.userId,
      displayName: member.displayName || 'Family Member',
      role: member.role || 'viewer',
      addedBy: member.addedBy || member.userId,
      addedAt: member.addedAt || now,
      isActive: true,
      email: member.email,
      avatar: member.avatar,
      avatarType: member.avatarType
    };

    // Sync denormalized arrays for security rules
    const { memberUserIds, editorUserIds, viewerUserIds, memberEmails } = syncDenormalizedUserIds([memberData]);

    const memberProfile: any = {
      id: memberProfileId,
      name: member.displayName || 'Family Member',
      ownerId: member.userId,
      type: 'personal',
      addresses: [],
      vehicles: [],
      insurancePolicies: [],
      emergencyContacts: [],
      members: [memberData],
      invitations: [],
      isPremium: false,
      memberUserIds,
      editorUserIds,
      viewerUserIds,
      memberEmails,
      createdAt: now,
      updatedAt: now
    };

    // Save to Firestore
    const cleaned = removeUndefinedValues(memberProfile);
    await setDoc(doc(db, PROFILES_COLLECTION, memberProfileId), cleaned);

    return memberProfileId;
  }

  /**
   * Create a new profile
   */
  static async createProfile(name: string, userId: string, profileType: 'personal' | 'business' = 'personal'): Promise<Profile> {
    const now = new Date().toISOString();
    const email = localStorage.getItem('userEmail') || '';

    // Build member object without undefined values
    const ownerMember: any = {
      userId: userId,
      displayName: name.trim().split(' ')[0],
      role: 'owner',
      addedBy: userId,
      addedAt: now,
      isActive: true,
    };
    // Only add email if it's a non-empty string
    if (email) {
      ownerMember.email = email;
    }

    // Sync denormalized arrays for security rules
    const { memberUserIds, editorUserIds, viewerUserIds, memberEmails } = syncDenormalizedUserIds([ownerMember]);

    const newProfile: any = {
      id: ProfileService.generateId(),
      name: name.trim(),
      ownerId: userId,
      type: profileType,
      addresses: [],
      vehicles: [],
      insurancePolicies: [],
      emergencyContacts: [],
      members: [ownerMember],
      invitations: [],
      isPremium: false,
      memberUserIds,
      editorUserIds,
      viewerUserIds,
      memberEmails,
      createdAt: now,
      updatedAt: now
    };

    await ProfileService.saveProfile(newProfile);
    return newProfile;
  }

  /**
   * Update profile in Firestore
   */
  static async updateProfile(profileId: string, updates: Partial<Profile>): Promise<void> {
    const profileRef = doc(db, PROFILES_COLLECTION, profileId);

    // Get the profile's ownerId for consistent encryption key
    // This ensures all data is encrypted with the same key (owner's ID) regardless of who is making the update
    const profileSnap = await getDocFromServer(profileRef);
    const profileData = profileSnap.exists() ? profileSnap.data() : null;
    const encryptionUserId = profileData?.ownerId || ProfileService.getCurrentUserId() || '';

    // Strip any base64 data: URLs that would blow past Firestore's 1MB limit
    const safeUpdates = stripDataUrls(updates);

    // Encrypt sensitive fields in vehicles and addresses if they're being updated
    // Always encrypt - data from loadProfile() is always decrypted plaintext
    const encryptedUpdates: any = { ...safeUpdates };

    // Encrypt profile-level dateOfBirth
    if (encryptedUpdates.dateOfBirth) {
      encryptedUpdates.dateOfBirth = await encryptionService.encrypt(encryptedUpdates.dateOfBirth, encryptionUserId);
    }

    if (encryptedUpdates.vehicles && encryptedUpdates.vehicles.length > 0) {
      encryptedUpdates.vehicles = await Promise.all(
        encryptedUpdates.vehicles.map((vehicle: Vehicle) => encryptVehicle(vehicle, encryptionUserId))
      );
    }

    if (encryptedUpdates.addresses && encryptedUpdates.addresses.length > 0) {
      encryptedUpdates.addresses = await Promise.all(
        encryptedUpdates.addresses.map((address: Address) => encryptAddress(address, encryptionUserId))
      );
    }

    // Encrypt ownerSecurity fields
    if (encryptedUpdates.ownerSecurity) {
      encryptedUpdates.ownerSecurity = await encryptOwnerSecurity(encryptedUpdates.ownerSecurity, encryptionUserId);
    }

    // Encrypt family members' importantDates and sync denormalized arrays
    if (encryptedUpdates.members && encryptedUpdates.members.length > 0) {
      encryptedUpdates.members = await Promise.all(
        encryptedUpdates.members.map((member: any) => encryptFamilyMember(member, encryptionUserId))
      );

      // IMPORTANT: Sync denormalized userIds arrays for security rules
      // These arrays are used by Firestore rules to check editor/viewer access
      const { memberUserIds, editorUserIds, viewerUserIds, memberEmails } = syncDenormalizedUserIds(encryptedUpdates.members);
      encryptedUpdates.memberUserIds = memberUserIds;
      encryptedUpdates.editorUserIds = editorUserIds;
      encryptedUpdates.viewerUserIds = viewerUserIds;
      encryptedUpdates.memberEmails = memberEmails;
    }

    // Recursively remove undefined values (including in nested objects/arrays)
    const cleanUpdates = removeUndefinedValues({
      ...encryptedUpdates,
      updatedAt: new Date().toISOString(),
    });

    // Debug: Log if any undefined values still exist (should not happen)
    const findUndefined = (obj: any, path: string = ''): string[] => {
      const issues: string[] = [];
      if (obj === undefined) {
        issues.push(path || 'root');
      } else if (obj !== null && typeof obj === 'object') {
        if (Array.isArray(obj)) {
          obj.forEach((item, idx) => {
            issues.push(...findUndefined(item, `${path}[${idx}]`));
          });
        } else {
          Object.entries(obj).forEach(([key, value]) => {
            issues.push(...findUndefined(value, path ? `${path}.${key}` : key));
          });
        }
      }
      return issues;
    };

    const undefinedPaths = findUndefined(cleanUpdates);
    if (undefinedPaths.length > 0) {
      console.error('ProfileService.updateProfile: Found undefined values at paths:', undefinedPaths);
      console.error('Update data:', JSON.stringify(cleanUpdates, (_k, v) => v === undefined ? '__UNDEFINED__' : v, 2));
    }

    try {
      await updateDoc(profileRef, cleanUpdates);
    } catch (error: any) {
      if (error?.message?.includes('exceeds the maximum allowed size')) {
        throw new Error(
          'Your profile has too much data to save. Try removing some photos or documents to free up space.',
        );
      }
      throw error;
    }
  }

  /**
   * Update a family member's avatar in the pilot's profile
   * Called when a family member signs in with Google to persist their profile picture
   *
   * Only updates avatar if member doesn't already have a custom-uploaded photo.
   * This preserves user's custom photo uploads across logins.
   */
  static async updateMemberAvatar(
    memberEmail: string,
    avatarUrl: string
  ): Promise<void> {
    try {
      // Query ONLY profiles where this user's email is in memberEmails array
      // This respects Firebase security rules - users can only read profiles they're members of
      const profilesRef = collection(db, PROFILES_COLLECTION);
      const memberQuery = query(profilesRef, where('memberEmails', 'array-contains', memberEmail.toLowerCase()));
      const profileSnapshot = await getDocs(memberQuery);

      for (const profileDoc of profileSnapshot.docs) {
        const profileData = profileDoc.data();
        const members = profileData.members || [];

        // Find member by email
        const memberIndex = members.findIndex(
          (m: any) => m.email?.toLowerCase() === memberEmail.toLowerCase()
        );

        if (memberIndex !== -1) {
          const existingMember = members[memberIndex];

          // Helper to check if an avatar URL is a custom upload (not a Google photo)
          const isCustomUploadedPhoto = (avatar: string | undefined | null): boolean => {
            if (!avatar) return false;
            // Custom photos can be:
            // 1. Stored in Firebase Storage (contain 'firebasestorage')
            // 2. Base64 data URLs (start with 'data:image/')
            // Google photos are from googleusercontent.com
            return avatar.includes('firebasestorage') || avatar.startsWith('data:image/');
          };

          // Only check if THIS MEMBER has a custom uploaded photo
          // Do NOT check profile-level avatar - that belongs to the pilot, not this member
          const hasCustomPhotoOnMember = isCustomUploadedPhoto(existingMember.avatar);

          if (hasCustomPhotoOnMember) {
            // Skip - preserve this member's custom uploaded photo
            continue;
          }

          // Update the member's avatar - clean existing member data first to remove any undefined values
          const updatedMembers = [...members];

          // Build clean member object without undefined values
          const cleanMember: any = {};
          for (const [key, value] of Object.entries(existingMember)) {
            if (value !== undefined) {
              cleanMember[key] = value;
            }
          }
          // Update avatar with Google photo (only if no custom photo exists)
          cleanMember.avatar = avatarUrl;

          updatedMembers[memberIndex] = cleanMember;

          await ProfileService.updateProfile(profileDoc.id, { members: updatedMembers });
          // Continue to update other profiles where this member exists
        }
      }
    } catch (error) {
      // Don't throw - avatar update is not critical for auth flow
      console.error('[updateMemberAvatar] Error updating member avatar:', error);
    }
  }

  /**
   * Delete profile from Firestore with cascade delete of dependent collections
   * This ensures no orphaned documents remain in:
   * - reminders
   * - healthProviders
   * - financialInstitutions
   * - insurancePolicies (separate collection)
   * - shareBundles
   */
  static async deleteProfile(profileId: string): Promise<void> {
    const batch = writeBatch(db);

    // 1. Delete all reminders for this profile
    try {
      const remindersQuery = query(collection(db, 'reminders'), where('profileId', '==', profileId));
      const remindersSnapshot = await getDocs(remindersQuery);
      remindersSnapshot.forEach(docSnap => {
        batch.delete(docSnap.ref);
      });
    } catch (error) {
      console.warn('Failed to query reminders for cascade delete:', error);
    }

    // 2. Delete all health providers for this profile
    try {
      const healthQuery = query(collection(db, 'healthProviders'), where('profileId', '==', profileId));
      const healthSnapshot = await getDocs(healthQuery);
      healthSnapshot.forEach(docSnap => {
        batch.delete(docSnap.ref);
      });
    } catch (error) {
      console.warn('Failed to query healthProviders for cascade delete:', error);
    }

    // 3. Delete all financial institutions for this profile
    try {
      const financialQuery = query(collection(db, 'financialInstitutions'), where('profileId', '==', profileId));
      const financialSnapshot = await getDocs(financialQuery);
      financialSnapshot.forEach(docSnap => {
        batch.delete(docSnap.ref);
      });
    } catch (error) {
      console.warn('Failed to query financialInstitutions for cascade delete:', error);
    }

    // 4. Delete all insurance policies (separate collection) for this profile
    try {
      const insuranceQuery = query(collection(db, 'insurancePolicies'), where('profileId', '==', profileId));
      const insuranceSnapshot = await getDocs(insuranceQuery);
      insuranceSnapshot.forEach(docSnap => {
        batch.delete(docSnap.ref);
      });
    } catch (error) {
      console.warn('Failed to query insurancePolicies for cascade delete:', error);
    }

    // 5. Delete all share bundles for this profile
    try {
      const bundlesQuery = query(collection(db, 'shareBundles'), where('profileId', '==', profileId));
      const bundlesSnapshot = await getDocs(bundlesQuery);
      bundlesSnapshot.forEach(docSnap => {
        batch.delete(docSnap.ref);
      });
    } catch (error) {
      console.warn('Failed to query shareBundles for cascade delete:', error);
    }

    // 6. Delete the profile document itself
    const profileRef = doc(db, PROFILES_COLLECTION, profileId);
    batch.delete(profileRef);

    // Commit all deletes atomically
    await batch.commit();

    // If we're deleting the current profile, clear it from localStorage
    const currentProfileId = ProfileService.getCurrentProfileId();
    if (currentProfileId === profileId) {
      localStorage.removeItem(CURRENT_PROFILE_KEY);
    }
  }

  /**
   * Add address to profile
   */
  static async addAddress(profileId: string, address: Omit<Address, 'id'>): Promise<string> {
    const profile = await ProfileService.loadProfile(profileId);

    if (!profile) {
      throw new Error('Profile not found');
    }

    // Check for duplicate address
    const isDuplicate = profile.addresses.some(existingAddress => {
      return (
        existingAddress.street.toLowerCase().trim() === address.street.toLowerCase().trim() &&
        existingAddress.city.toLowerCase().trim() === address.city.toLowerCase().trim() &&
        existingAddress.state.toLowerCase().trim() === address.state.toLowerCase().trim() &&
        existingAddress.zipCode.trim() === address.zipCode.trim()
      );
    });

    if (isDuplicate) {
      throw new Error('This address already exists in your profile');
    }

    const newAddress: Address = {
      ...address,
      id: ProfileService.generateId(),
      createdAt: address.createdAt || new Date().toISOString(),
    };

    await ProfileService.updateProfile(profileId, {
      addresses: [...profile.addresses, newAddress]
    });

    return newAddress.id;
  }

  /**
   * Update address in profile
   */
  static async updateAddress(profileId: string, addressId: string, updates: Partial<Address>): Promise<void> {
    const profile = await ProfileService.loadProfile(profileId);

    if (!profile) {
      throw new Error('Profile not found');
    }

    const addressIndex = profile.addresses.findIndex(a => a.id === addressId);
    if (addressIndex === -1) {
      throw new Error('Address not found');
    }

    // Check for duplicate address (excluding the current address being updated)
    if (updates.street || updates.city || updates.state || updates.zipCode) {
      const updatedAddress = { ...profile.addresses[addressIndex], ...updates };
      const isDuplicate = profile.addresses.some((existingAddress, index) => {
        if (index === addressIndex) return false; // Skip the current address
        return (
          existingAddress.street.toLowerCase().trim() === updatedAddress.street.toLowerCase().trim() &&
          existingAddress.city.toLowerCase().trim() === updatedAddress.city.toLowerCase().trim() &&
          existingAddress.state.toLowerCase().trim() === updatedAddress.state.toLowerCase().trim() &&
          existingAddress.zipCode.trim() === updatedAddress.zipCode.trim()
        );
      });

      if (isDuplicate) {
        throw new Error('This address already exists in your profile');
      }
    }

    const updatedAddresses = [...profile.addresses];
    updatedAddresses[addressIndex] = {
      ...updatedAddresses[addressIndex],
      ...updates,
      id: addressId
    };

    await ProfileService.updateProfile(profileId, {
      addresses: updatedAddresses
    });
  }

  /**
   * Delete address from profile
   */
  static async deleteAddress(profileId: string, addressId: string): Promise<void> {
    const profile = await ProfileService.loadProfile(profileId);

    if (!profile) {
      throw new Error('Profile not found');
    }

    await ProfileService.updateProfile(profileId, {
      addresses: profile.addresses.filter(a => a.id !== addressId)
    });
  }

  // Personal item management methods
  static async addPersonalItem(profileId: string, item: Omit<import('../types/profile').PersonalItem, 'id'>): Promise<string> {
    const profile = await ProfileService.loadProfile(profileId);
    if (!profile) throw new Error('Profile not found');

    const newItem: import('../types/profile').PersonalItem = {
      ...item,
      id: ProfileService.generateId(),
    };

    await ProfileService.updateProfile(profileId, {
      personalItems: [...(profile.personalItems || []), newItem],
    });

    return newItem.id;
  }

  static async updatePersonalItem(profileId: string, itemId: string, updates: Partial<import('../types/profile').PersonalItem>): Promise<void> {
    const profile = await ProfileService.loadProfile(profileId);
    if (!profile) throw new Error('Profile not found');

    const items = [...(profile.personalItems || [])];
    const idx = items.findIndex(i => i.id === itemId);
    if (idx === -1) throw new Error('Personal item not found');

    items[idx] = { ...items[idx], ...updates, id: itemId };

    await ProfileService.updateProfile(profileId, { personalItems: items });
  }

  static async deletePersonalItem(profileId: string, itemId: string): Promise<void> {
    const profile = await ProfileService.loadProfile(profileId);
    if (!profile) throw new Error('Profile not found');

    await ProfileService.updateProfile(profileId, {
      personalItems: (profile.personalItems || []).filter(i => i.id !== itemId),
    });
  }

  // Vehicle management methods
  static async addVehicle(profileId: string, vehicle: Omit<Vehicle, 'id'>): Promise<void> {
    const profile = await ProfileService.loadProfile(profileId);

    if (!profile) {
      throw new Error('Profile not found');
    }

    // Check for duplicate vehicle (same make, model, year combination or same VIN)
    const isDuplicate = profile.vehicles.some(existingVehicle => {
      // If VIN is provided and matches, it's a duplicate
      if (vehicle.vin && existingVehicle.vin &&
          vehicle.vin.toLowerCase().trim() === existingVehicle.vin.toLowerCase().trim()) {
        return true;
      }
      // Check for same make, model, year combination
      return (
        existingVehicle.make.toLowerCase().trim() === vehicle.make.toLowerCase().trim() &&
        existingVehicle.model.toLowerCase().trim() === vehicle.model.toLowerCase().trim() &&
        existingVehicle.year === vehicle.year
      );
    });

    if (isDuplicate) {
      throw new Error('This vehicle already exists in your profile');
    }

    const newVehicle: Vehicle = {
      ...vehicle,
      id: ProfileService.generateId()
    };

    await ProfileService.updateProfile(profileId, {
      vehicles: [...profile.vehicles, newVehicle]
    });
  }

  static async updateVehicle(profileId: string, vehicleId: string, updates: Partial<Vehicle>): Promise<void> {
    const profile = await ProfileService.loadProfile(profileId);

    if (!profile) {
      throw new Error('Profile not found');
    }

    const vehicleIndex = profile.vehicles.findIndex(v => v.id === vehicleId);
    if (vehicleIndex === -1) {
      throw new Error('Vehicle not found');
    }

    // Check for duplicate vehicle (excluding the current vehicle being updated)
    if (updates.make || updates.model || updates.year || updates.vin) {
      const updatedVehicle = { ...profile.vehicles[vehicleIndex], ...updates };
      const isDuplicate = profile.vehicles.some((existingVehicle, index) => {
        if (index === vehicleIndex) return false; // Skip the current vehicle
        // If VIN is provided and matches, it's a duplicate
        if (updatedVehicle.vin && existingVehicle.vin &&
            updatedVehicle.vin.toLowerCase().trim() === existingVehicle.vin.toLowerCase().trim()) {
          return true;
        }
        // Check for same make, model, year combination
        return (
          existingVehicle.make.toLowerCase().trim() === updatedVehicle.make.toLowerCase().trim() &&
          existingVehicle.model.toLowerCase().trim() === updatedVehicle.model.toLowerCase().trim() &&
          existingVehicle.year === updatedVehicle.year
        );
      });

      if (isDuplicate) {
        throw new Error('This vehicle already exists in your profile');
      }
    }

    const updatedVehicles = [...profile.vehicles];
    updatedVehicles[vehicleIndex] = {
      ...updatedVehicles[vehicleIndex],
      ...updates,
      id: vehicleId
    };

    await ProfileService.updateProfile(profileId, {
      vehicles: updatedVehicles
    });
  }

  static async deleteVehicle(profileId: string, vehicleId: string): Promise<void> {
    const profile = await ProfileService.loadProfile(profileId);

    if (!profile) {
      throw new Error('Profile not found');
    }

    await ProfileService.updateProfile(profileId, {
      vehicles: profile.vehicles.filter(v => v.id !== vehicleId)
    });
  }

  // Insurance policy management methods
  static async addInsurancePolicy(profileId: string, policy: Omit<InsurancePolicy, 'id'>): Promise<void> {
    const profile = await ProfileService.loadProfile(profileId);

    if (!profile) {
      throw new Error('Profile not found');
    }

    const newPolicy: InsurancePolicy = {
      ...policy,
      id: ProfileService.generateId()
    };

    await ProfileService.updateProfile(profileId, {
      insurancePolicies: [...profile.insurancePolicies, newPolicy]
    });
  }

  static async updateInsurancePolicy(profileId: string, policyId: string, updates: Partial<InsurancePolicy>): Promise<void> {
    const profile = await ProfileService.loadProfile(profileId);

    if (!profile) {
      throw new Error('Profile not found');
    }

    const policyIndex = profile.insurancePolicies.findIndex(p => p.id === policyId);
    if (policyIndex === -1) {
      throw new Error('Insurance policy not found');
    }

    const updatedPolicies = [...profile.insurancePolicies];
    updatedPolicies[policyIndex] = {
      ...updatedPolicies[policyIndex],
      ...updates,
      id: policyId
    };

    await ProfileService.updateProfile(profileId, {
      insurancePolicies: updatedPolicies
    });
  }

  static async deleteInsurancePolicy(profileId: string, policyId: string): Promise<void> {
    const profile = await ProfileService.loadProfile(profileId);

    if (!profile) {
      throw new Error('Profile not found');
    }

    // Clean up orphaned references: clear linkedInsuranceId from addresses and vehicles
    // that reference this policy being deleted
    const updatedAddresses = profile.addresses.map(address => {
      if (address.linkedInsuranceId === policyId) {
        const { linkedInsuranceId, ...rest } = address;
        return rest as Address;
      }
      return address;
    });

    const updatedVehicles = profile.vehicles.map(vehicle => {
      if (vehicle.linkedInsuranceId === policyId) {
        const { linkedInsuranceId, ...rest } = vehicle;
        return rest as Vehicle;
      }
      return vehicle;
    });

    await ProfileService.updateProfile(profileId, {
      insurancePolicies: profile.insurancePolicies.filter(p => p.id !== policyId),
      addresses: updatedAddresses,
      vehicles: updatedVehicles
    });
  }

  // Emergency contact management methods
  static async addEmergencyContact(profileId: string, contact: Omit<EmergencyContact, 'id'>): Promise<void> {
    const profile = await ProfileService.loadProfile(profileId);

    if (!profile) {
      throw new Error('Profile not found');
    }

    const newContact: EmergencyContact = {
      ...contact,
      id: ProfileService.generateId()
    };

    await ProfileService.updateProfile(profileId, {
      emergencyContacts: [...profile.emergencyContacts, newContact]
    });
  }

  static async updateEmergencyContact(profileId: string, contactId: string, updates: Partial<EmergencyContact>): Promise<void> {
    const profile = await ProfileService.loadProfile(profileId);

    if (!profile) {
      throw new Error('Profile not found');
    }

    const contactIndex = profile.emergencyContacts.findIndex(c => c.id === contactId);
    if (contactIndex === -1) {
      throw new Error('Emergency contact not found');
    }

    const updatedContacts = [...profile.emergencyContacts];
    updatedContacts[contactIndex] = {
      ...updatedContacts[contactIndex],
      ...updates,
      id: contactId
    };

    await ProfileService.updateProfile(profileId, {
      emergencyContacts: updatedContacts
    });
  }

  static async deleteEmergencyContact(profileId: string, contactId: string): Promise<void> {
    const profile = await ProfileService.loadProfile(profileId);

    if (!profile) {
      throw new Error('Profile not found');
    }

    await ProfileService.updateProfile(profileId, {
      emergencyContacts: profile.emergencyContacts.filter(c => c.id !== contactId)
    });
  }

  /**
   * Migrate existing profiles to add denormalized userIds arrays
   * This is needed for existing profiles that were created before this feature
   * Should be called once on app startup or as an admin action
   */
  static async migrateProfilesWithDenormalizedArrays(): Promise<{ migrated: number; skipped: number }> {
    const profilesRef = collection(db, PROFILES_COLLECTION);
    const profileSnapshot = await getDocs(profilesRef);
    let migrated = 0;
    let skipped = 0;

    for (const profileDoc of profileSnapshot.docs) {
      const profileData = profileDoc.data();

      // Skip if already has ALL the denormalized arrays (including memberEmails)
      if (profileData.editorUserIds !== undefined &&
          profileData.viewerUserIds !== undefined &&
          profileData.memberEmails !== undefined) {
        skipped++;
        continue;
      }

      // Sync denormalized arrays from members
      const members = profileData.members || [];
      const { memberUserIds, editorUserIds, viewerUserIds, memberEmails } = syncDenormalizedUserIds(members);

      // Update the profile with denormalized arrays
      await updateDoc(doc(db, PROFILES_COLLECTION, profileDoc.id), {
        memberUserIds,
        editorUserIds,
        viewerUserIds,
        memberEmails
      });

      migrated++;
    }

    return { migrated, skipped };
  }

  /**
   * Link insurance policy to vehicle/address with transaction support
   * This ensures both the policy and linked entity are updated atomically
   * Prevents race conditions and partial updates
   */
  static async linkInsuranceToEntity(
    profileId: string,
    policyId: string,
    linkType: 'vehicle' | 'address',
    entityId: string | undefined,
    previousEntityId?: string
  ): Promise<void> {
    const profileRef = doc(db, PROFILES_COLLECTION, profileId);

    await runTransaction(db, async (transaction) => {
      const profileDoc = await transaction.get(profileRef);
      if (!profileDoc.exists()) {
        throw new Error('Profile not found');
      }

      const profile = profileDoc.data() as Profile;

      // Find and update the insurance policy
      const policyIndex = profile.insurancePolicies.findIndex(p => p.id === policyId);
      if (policyIndex === -1) {
        throw new Error('Insurance policy not found');
      }

      const updatedPolicies = [...profile.insurancePolicies];
      const linkField = linkType === 'vehicle' ? 'linkedVehicleId' : 'linkedAddressId';

      // Update the policy with the new link
      updatedPolicies[policyIndex] = {
        ...updatedPolicies[policyIndex],
        [linkField]: entityId
      };

      // Update the entity arrays (vehicle or address)
      let updatedVehicles = [...profile.vehicles];
      let updatedAddresses = [...profile.addresses];

      if (linkType === 'vehicle') {
        // Clear old vehicle link if changing
        if (previousEntityId && previousEntityId !== entityId) {
          updatedVehicles = updatedVehicles.map(v =>
            v.id === previousEntityId ? { ...v, linkedInsuranceId: undefined } : v
          );
        }
        // Set new vehicle link
        if (entityId) {
          updatedVehicles = updatedVehicles.map(v =>
            v.id === entityId ? { ...v, linkedInsuranceId: policyId } : v
          );
        }
      } else {
        // Clear old address link if changing
        if (previousEntityId && previousEntityId !== entityId) {
          updatedAddresses = updatedAddresses.map(a =>
            a.id === previousEntityId ? { ...a, linkedInsuranceId: undefined } : a
          );
        }
        // Set new address link
        if (entityId) {
          updatedAddresses = updatedAddresses.map(a =>
            a.id === entityId ? { ...a, linkedInsuranceId: policyId } : a
          );
        }
      }

      // Commit the transaction with all updates
      transaction.update(profileRef, removeUndefinedValues({
        insurancePolicies: updatedPolicies,
        vehicles: updatedVehicles,
        addresses: updatedAddresses,
        updatedAt: new Date().toISOString()
      }));
    });
  }

  // ── Household Members ──────────────────────────────────────────

  static readonly HOUSEHOLD_COLORS = [
    '#007AFF', '#FF9500', '#32ADE6', '#5856D6',
    '#30B0C7', '#AF52DE', '#34C759',
  ];

  static getNextHouseholdColor(existing: HouseholdMember[]): string {
    const usedColors = new Set(existing.map(m => m.color));
    return ProfileService.HOUSEHOLD_COLORS.find(c => !usedColors.has(c))
      || ProfileService.HOUSEHOLD_COLORS[existing.length % ProfileService.HOUSEHOLD_COLORS.length];
  }

  static async addHouseholdMember(profileId: string, member: Omit<HouseholdMember, 'id' | 'createdAt' | 'color'>): Promise<HouseholdMember> {
    const profile = await ProfileService.loadProfile(profileId);
    if (!profile) throw new Error('Profile not found');

    const existing = profile.householdMembers || [];
    const newMember: HouseholdMember = {
      ...member,
      id: `hm_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      color: ProfileService.getNextHouseholdColor(existing),
      createdAt: new Date().toISOString(),
    };

    await ProfileService.updateProfile(profileId, {
      householdMembers: [...existing, newMember],
    });

    return newMember;
  }

  static async removeHouseholdMember(profileId: string, memberId: string): Promise<void> {
    const profile = await ProfileService.loadProfile(profileId);
    if (!profile) throw new Error('Profile not found');

    await ProfileService.updateProfile(profileId, {
      householdMembers: (profile.householdMembers || []).filter(m => m.id !== memberId),
    });
  }
}
